// Google Analytics and GTM utility functions
// This file provides helper functions for tracking events and page views

// Check if GTM is available
const isGTMAvailable = () => {
  return typeof window !== 'undefined' && window.dataLayer;
};

// Push data to GTM dataLayer
export const pushToDataLayer = (data) => {
  if (isGTMAvailable()) {
    window.dataLayer.push(data);
  }
};

// Track page views
export const trackPageView = (pageName, pagePath) => {
  pushToDataLayer({
    event: 'page_view',
    page_name: pageName,
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title
  });
};

// Track custom events
export const trackEvent = (eventName, eventCategory, eventAction, eventLabel, value) => {
  pushToDataLayer({
    event: eventName,
    event_category: eventCategory,
    event_action: eventAction,
    event_label: eventLabel,
    value: value
  });
};

// Track form submissions
export const trackFormSubmission = (formName, formType) => {
  trackEvent('form_submit', 'engagement', 'form_submission', formName, 1);
  pushToDataLayer({
    event: 'form_submit',
    form_name: formName,
    form_type: formType
  });
};

// Track button clicks
export const trackButtonClick = (buttonName, buttonLocation) => {
  trackEvent('button_click', 'engagement', 'button_click', buttonName, 1);
  pushToDataLayer({
    event: 'button_click',
    button_name: buttonName,
    button_location: buttonLocation
  });
};

// Track navigation clicks
export const trackNavigationClick = (linkName, linkDestination) => {
  trackEvent('navigation_click', 'engagement', 'navigation', linkName, 1);
  pushToDataLayer({
    event: 'navigation_click',
    link_name: linkName,
    link_destination: linkDestination
  });
};

// Track user interactions
export const trackUserInteraction = (interactionType, interactionDetails) => {
  trackEvent('user_interaction', 'engagement', interactionType, interactionDetails, 1);
  pushToDataLayer({
    event: 'user_interaction',
    interaction_type: interactionType,
    interaction_details: interactionDetails
  });
};

// Track business events specific to PharmaChargeback
export const trackChargebackEvent = (eventType, details) => {
  pushToDataLayer({
    event: 'chargeback_event',
    chargeback_event_type: eventType,
    chargeback_details: details
  });
};

// Track contract events
export const trackContractEvent = (eventType, details) => {
  pushToDataLayer({
    event: 'contract_event',
    contract_event_type: eventType,
    contract_details: details
  });
};

// Track EDI events
export const trackEDIEvent = (eventType, details) => {
  pushToDataLayer({
    event: 'edi_event',
    edi_event_type: eventType,
    edi_details: details
  });
};

// Track user authentication events
export const trackAuthEvent = (eventType, userType) => {
  pushToDataLayer({
    event: 'auth_event',
    auth_event_type: eventType,
    user_type: userType
  });
};

// Track file upload events
export const trackFileUpload = (fileType, fileSize, uploadType) => {
  trackEvent('file_upload', 'engagement', 'file_upload', fileType, 1);
  pushToDataLayer({
    event: 'file_upload',
    file_type: fileType,
    file_size: fileSize,
    upload_type: uploadType
  });
};

// Track search events
export const trackSearch = (searchTerm, searchResults, searchCategory) => {
  trackEvent('search', 'engagement', 'search', searchTerm, searchResults);
  pushToDataLayer({
    event: 'search',
    search_term: searchTerm,
    search_results: searchResults,
    search_category: searchCategory
  });
};

// Track error events
export const trackError = (errorType, errorMessage, errorLocation) => {
  pushToDataLayer({
    event: 'error',
    error_type: errorType,
    error_message: errorMessage,
    error_location: errorLocation
  });
};

// Track conversion events
export const trackConversion = (conversionType, conversionValue, conversionDetails) => {
  trackEvent('conversion', 'conversion', conversionType, conversionDetails, conversionValue);
  pushToDataLayer({
    event: 'conversion',
    conversion_type: conversionType,
    conversion_value: conversionValue,
    conversion_details: conversionDetails
  });
};

// Track demo requests
export const trackDemoRequest = (demoType, contactInfo) => {
  trackConversion('demo_request', 1, demoType);
  pushToDataLayer({
    event: 'demo_request',
    demo_type: demoType,
    contact_info: contactInfo
  });
};

// Track trial signups
export const trackTrialSignup = (userType, signupMethod) => {
  trackConversion('trial_signup', 1, userType);
  pushToDataLayer({
    event: 'trial_signup',
    user_type: userType,
    signup_method: signupMethod
  });
};

// Track contact form submissions
export const trackContactForm = (formType, inquiryType) => {
  trackFormSubmission('contact_form', formType);
  pushToDataLayer({
    event: 'contact_form_submit',
    form_type: formType,
    inquiry_type: inquiryType
  });
};

// Initialize GTM (if not already initialized)
export const initializeGTM = () => {
  if (isGTMAvailable()) {
    // GTM is already initialized
    return;
  }
  
  // This would only run if GTM wasn't properly loaded
  console.warn('Google Tag Manager not detected');
};

// Set user properties
export const setUserProperties = (userId, userType, userRole) => {
  pushToDataLayer({
    event: 'user_properties',
    user_id: userId,
    user_type: userType,
    user_role: userRole
  });
};

// Track session duration
export const trackSessionDuration = (duration) => {
  pushToDataLayer({
    event: 'session_duration',
    session_duration: duration
  });
};

export default {
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackButtonClick,
  trackNavigationClick,
  trackUserInteraction,
  trackChargebackEvent,
  trackContractEvent,
  trackEDIEvent,
  trackAuthEvent,
  trackFileUpload,
  trackSearch,
  trackError,
  trackConversion,
  trackDemoRequest,
  trackTrialSignup,
  trackContactForm,
  initializeGTM,
  setUserProperties,
  trackSessionDuration
};
